import {
  type Violation,
  type ViolationDetail,
  type Paginated,
  type ViolationAppealDecideRequest,
  type ViolationDismissRequest,
  type ViolationEnforceRequest,
  type ViolationType,
  type ViolationTypeCreateRequest,
  type ViolationTypeDetail,
  type ViolationTypeUpdateRequest,
  type ViolationTypesListQuerySchema,
  type ViolationsListQuerySchema,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { EVENT_BUS } from '@/lib/event-bus';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

import { movedAccountStatus } from '../../utils/violation-consequence/violation-consequence.util';

/** The contracts' own input shapes (U3) — never a hand-written copy of the filters. */
type ViolationsParams = z.input<typeof ViolationsListQuerySchema>;
type ViolationTypesParams = z.input<typeof ViolationTypesListQuerySchema>;

/**
 * The AI-moderation queue and the rule catalogue behind it.
 *
 * ## One api for two resources, and that is the whole point
 *
 * A rule's `action` is what every queue row displays as its configured
 * consequence, and what a confirmation dialog promises will happen. Editing a
 * rule therefore invalidates the **queue**, not merely the catalogue — and one
 * api's `invalidatesTags` cannot reach another's cache (P17c). Splitting these
 * into two apis would have made the most important reactive edge in the module
 * an event-bus round trip to work around a boundary nothing needed. P10.6d set
 * the precedent when question options were injected into the questions api for
 * exactly this reason.
 *
 * ## Every decision returns the decided row, and the screen still re-reads
 *
 * The three verdicts answer with the freshly decided violation, and writing that
 * straight into the cache would save a round trip. It is deliberately not done,
 * for the reason `moderationApi`, `trustApi` and `verificationApi` all give:
 * rendering from a mutation response gives the screen two sources for one row,
 * and they disagree the moment a colleague acts a second later.
 *
 * ## It announces twice, because two different things happened
 *
 * `moderation.violation.decided` is this module's news. `members.status.changed`
 * is the shared lifecycle fact, published **only** when the row says the account
 * actually moved — a suspension or ban going on, or an overturn taking one off.
 * An upheld appeal and a dismissal move nothing, and telling subscribers
 * otherwise would have them re-read a status that did not change (W5).
 */
export const violationsApi = createApi({
  reducerPath: 'violationsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Violations', 'Violation', 'ViolationTypes', 'ViolationType'],
  endpoints: (builder) => ({
    /**
     * The queue.
     *
     * No `sort`/`order` ever reaches this: `ViolationRepository`
     * orders `detectedAt asc, id asc` unconditionally and ignores them, so
     * `useListUrlState` is given no `defaultSort` and no column carries a
     * `sortKey` (P19b).
     */
    listViolations: builder.query<Paginated<Violation>, ViolationsParams>({
      query: (params) => ({ url: '/admin/moderation/violations', method: 'GET', params }),
      providesTags: ['Violations'],
    }),

    /**
     * One violation, with the message that caused it.
     *
     * A separate request rather than the row the queue already holds: the
     * evidence is on the detail alone, and a dialog reading its own entry cannot
     * be looking at a case the poll has since moved on from.
     */
    violation: builder.query<ViolationDetail, string>({
      query: (id) => ({ url: `/admin/moderation/violations/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'Violation' as const, id }],
    }),

    enforceViolation: builder.mutation<ViolationDetail, { id: string } & ViolationEnforceRequest>({
      query: ({ id, ...body }) => ({
        url: `/admin/moderation/violations/${id}/enforce`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Violation' as const, id },
        'Violations' as const,
      ],
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceDecision(queryFulfilled);
      },
    }),

    dismissViolation: builder.mutation<ViolationDetail, { id: string } & ViolationDismissRequest>({
      query: ({ id, ...body }) => ({
        url: `/admin/moderation/violations/${id}/dismiss`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Violation' as const, id },
        'Violations' as const,
      ],
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceDecision(queryFulfilled);
      },
    }),

    decideAppeal: builder.mutation<ViolationDetail, { id: string } & ViolationAppealDecideRequest>({
      query: ({ id, ...body }) => ({
        url: `/admin/moderation/violations/${id}/appeal`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Violation' as const, id },
        'Violations' as const,
      ],
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceDecision(queryFulfilled);
      },
    }),

    /** The catalogue. This list DOES honour `sort`/`order` and searches on `key`. */
    listViolationTypes: builder.query<Paginated<ViolationType>, ViolationTypesParams>({
      query: (params) => ({ url: '/admin/moderation/violation-types', method: 'GET', params }),
      providesTags: ['ViolationTypes'],
    }),

    /** One rule with EVERY locale — what the ar/en tabs prefill from (W7). */
    violationType: builder.query<ViolationTypeDetail, string>({
      query: (id) => ({ url: `/admin/moderation/violation-types/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'ViolationType' as const, id }],
    }),

    createViolationType: builder.mutation<ViolationTypeDetail, ViolationTypeCreateRequest>({
      query: (body) => ({ url: '/admin/moderation/violation-types', method: 'POST', body }),
      // No queue invalidation: a brand-new rule has judged nothing yet, so no row
      // on any page is displaying it.
      invalidatesTags: ['ViolationTypes'],
    }),

    updateViolationType: builder.mutation<
      ViolationTypeDetail,
      { id: string; body: ViolationTypeUpdateRequest }
    >({
      query: ({ id, body }) => ({
        url: `/admin/moderation/violation-types/${id}`,
        method: 'PATCH',
        body,
      }),
      /**
       * Both queue tags, and they are the reason this api is not two.
       *
       * "Changing a type's action in admin changes behaviour without deploy" is
       * P20's own clause, and the moderator must SEE that: every queue row of
       * this rule displays its configured consequence, and an open case dialog
       * promises what confirming will do.
       */
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'ViolationType' as const, id },
        'ViolationTypes' as const,
        'Violations' as const,
        'Violation' as const,
      ],
    }),
  }),
});

/**
 * Publishes what a decision actually did.
 *
 * Shared by the three verdicts because the rule is the same for all of them and
 * lives in one place: the row the api answered with says whether an account
 * moved, so the button that was pressed is never what decides.
 */
async function announceDecision(queryFulfilled: Promise<{ data: ViolationDetail }>): Promise<void> {
  try {
    const { data } = await queryFulfilled;

    EVENT_BUS.publish({
      type: 'moderation.violation.decided',
      offenderUserId: data.offenderUserId,
    });

    if (movedAccountStatus(data)) {
      EVENT_BUS.publish({ type: 'members.status.changed', userId: data.offenderUserId });
    }
  } catch {
    // Refused — nothing happened, so there is nothing to announce. Swallowed
    // deliberately: `onQueryStarted` has no caller, so a rejection escaping it
    // becomes an unhandled rejection. The failure is already the caller's,
    // through `unwrap()`.
  }
}

export const {
  useListViolationsQuery,
  useViolationQuery,
  useEnforceViolationMutation,
  useDismissViolationMutation,
  useDecideAppealMutation,
  useListViolationTypesQuery,
  useLazyListViolationTypesQuery,
  useViolationTypeQuery,
  useCreateViolationTypeMutation,
  useUpdateViolationTypeMutation,
} = violationsApi;
